import moment from 'moment';
import _ from 'lodash';

import { toInterval } from 'components/V5Gadget/toAutoInterval';

type ShiftUnit = 'hour' | 'day' | 'week' | 'month' | 'year';

export interface GroupResponse {
  rows: MetricsResponse;
  // The date range the rows were asked for
  dateScope: DateRangeInput;
  // Asked for an earlier period than the chart's
  isOffset: boolean;
}

/**
 * The row field that places a row on the chart's axis, or undefined when the
 * chart has no axis to line up (a single total)
 */
export const getAlignKey = (
  chartDef: V5ChartDefinition,
): string | undefined => {
  const { dimensionA } = chartDef;
  if (!dimensionA) {
    return undefined;
  }
  if (dimensionA.isGroupByDayOfWeek || chartDef.groupByDayOfWeek) {
    return 'dayOfWeek';
  }
  if (dimensionA.isGroupByMonth) {
    return 'month';
  }
  if (chartDef.trendByHourInterval || dimensionA.rangeInput) {
    return 'key';
  }
  if (dimensionA.fieldType === 'date') {
    return 'date';
  }
  return dimensionA.field;
};

const CALENDAR_UNITS: ShiftUnit[] = ['hour', 'day', 'week', 'month', 'year'];

const getShiftUnit = ({
  rows,
  chartDef,
  autoInterval,
}: {
  rows: MetricsResponse;
  chartDef: V5ChartDefinition;
  autoInterval?: AutoInterval;
}): ShiftUnit => {
  const fromRows =
    rows.length > 0 && typeof rows[0].interval === 'string'
      ? toInterval(rows[0].interval)
      : undefined;
  if (fromRows) {
    return fromRows;
  }
  const configured = autoInterval
    ? autoInterval.interval
    : chartDef.trendByCalendarInterval;
  return CALENDAR_UNITS.find((u) => u === configured) || 'day';
};

const shiftDate = (date: string, amount: number, unit: ShiftUnit): string => {
  const shifted = moment.utc(date).add(amount, unit);
  if (date.length <= 10) {
    return shifted.format('YYYY-MM-DD');
  }
  return shifted.format(
    date.endsWith('Z') ? 'YYYY-MM-DDTHH:mm:ss[Z]' : 'YYYY-MM-DDTHH:mm:ss',
  );
};

/**
 * Moves an earlier period's rows onto the chart's period: last year's March
 * bucket becomes this year's, so every series shares one date axis.
 */
export const shiftOntoCurrentPeriod = ({
  response,
  currentDateScope,
  chartDef,
  autoInterval,
}: {
  response: GroupResponse;
  currentDateScope: DateRangeInput;
  chartDef: V5ChartDefinition;
  autoInterval?: AutoInterval;
}): MetricsResponse => {
  const { rows, dateScope } = response;
  if (!currentDateScope.startDate || !dateScope.startDate) {
    return rows;
  }
  const unit = getShiftUnit({ rows, chartDef, autoInterval });
  const amount = moment
    .utc(currentDateScope.startDate)
    .diff(moment.utc(dateScope.startDate), unit);
  if (amount <= 0) {
    return rows;
  }
  return rows.map((row) =>
    typeof row.date === 'string'
      ? { ...row, date: shiftDate(row.date, amount, unit) }
      : row,
  );
};

// What each row carries besides its values: chart code reads these off rows
const STAMPED_FIELDS = ['interval', 'filterInput', 'dateScope'];

/**
 * Lines up separately fetched responses (the chart's own query plus one per
 * group of series query overrides) so every series has a row, in the same
 * order, for every point on the axis. Chart code places series data by index.
 *
 * - Date axes: earlier periods are moved onto the chart's period first. The
 *   axis is the union of the dates asked for the chart's period; when every
 *   response is an earlier period, the union of all of them.
 * - Other axes: the union of every response's keys, first response first, so
 *   series filtered to different groups (top drivers in each division) all
 *   show.
 */
const alignGroupResponses = ({
  chartDef,
  autoInterval,
  currentDateScope,
  responses,
}: {
  chartDef: V5ChartDefinition;
  autoInterval?: AutoInterval;
  currentDateScope: DateRangeInput;
  responses: GroupResponse[];
}): MetricsResponse[] => {
  const alignKey = getAlignKey(chartDef);
  if (!alignKey || responses.length < 2) {
    return responses.map((r) => r.rows);
  }

  const isDateAxis = alignKey === 'date';
  const shifted = responses.map((response) =>
    isDateAxis && response.isOffset
      ? shiftOntoCurrentPeriod({
          response,
          currentDateScope,
          chartDef,
          autoInterval,
        })
      : response.rows,
  );

  const axisSources = (() => {
    if (!isDateAxis) {
      return shifted;
    }
    const current = shifted.filter((_rows, i) => !responses[i].isOffset);
    return current.length > 0 ? current : shifted;
  })();
  const keysFor = (rows: MetricsResponse) =>
    rows
      .map((row) => row[alignKey])
      .filter((k) => k !== undefined && k !== null)
      .map((k) => String(k));
  const unionKeys = _.uniq(axisSources.flatMap(keysFor));
  const axis = isDateAxis ? [...unionKeys].sort() : unionKeys;

  return shifted.map((rows) => {
    const byKey = new Map<string, MetricResponse>();
    rows.forEach((row) => {
      const k = row[alignKey];
      if (k !== undefined && k !== null && !byKey.has(String(k))) {
        byKey.set(String(k), row);
      }
    });
    const stamp = rows.length > 0 ? _.pick(rows[0], STAMPED_FIELDS) : {};
    return axis.map(
      (k) => byKey.get(k) || { ...stamp, [alignKey]: k },
    ) as MetricsResponse;
  });
};

export default alignGroupResponses;
